import { ReactNode } from 'react';
import { Progress } from '@/components/ui/progress';
import { useI18n } from '@/hooks/useI18n';
import { IPaymentPlan } from '@/lib/builder/models/payment-plan.model';
import { toCents, sumCents } from '@/lib/builder/utils/payment-plan.utils';

interface PaymentPlanSummaryProps {
    plan: IPaymentPlan;
    /** Formattatore passato dal chiamante: il planner e la vista pubblica ne usano uno diverso. */
    formatAmount: (value: number) => string;
    /** Azione allineata a destra sulla riga della barra: usata dal TD per "Modifica". */
    action?: ReactNode;
}

/**
 * Incassato / residuo / avanzamento. Condiviso fra la card del checkout (TD) e il
 * pannello dell'itinerario condiviso (cliente): sono la stessa informazione, non ha
 * senso scriverla due volte.
 */
export const PaymentPlanSummary = ({ plan, formatAmount, action }: PaymentPlanSummaryProps) => {
    const { t } = useI18n();

    const paidCents = sumCents(plan.installments.filter((i) => i.status === 'paid'));
    const totalCents = toCents(plan.total_amount);
    const progress = totalCents > 0 ? Math.round((paidCents / totalCents) * 100) : 0;

    return (
        <div className="flex flex-wrap items-end gap-x-5 gap-y-2">
            <div className="flex flex-col leading-tight">
                <span className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                    {t('checkout.paymentPlan.collected')}
                </span>
                <span className="text-lg font-semibold text-gray-900 tabular-nums">
                    {formatAmount(paidCents / 100)}
                </span>
            </div>

            <div className="flex flex-col leading-tight">
                <span className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                    {t('checkout.paymentPlan.remaining')}
                </span>
                <span className="text-lg font-medium text-gray-600 tabular-nums">
                    {formatAmount((totalCents - paidCents) / 100)}
                </span>
            </div>

            <div className="flex flex-col gap-1.5 flex-1 min-w-[160px] pb-1.5">
                <span className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
                    {t('checkout.paymentPlan.progress')}
                </span>
                <Progress value={progress} className="h-1.5" />
            </div>

            {action && <div className="flex-shrink-0 pb-0.5">{action}</div>}
        </div>
    );
};
